import {
  CRITERION_IMPORTANCE_LABELS,
  MUST_HAVE_CAP,
  MUST_HAVE_MIN_MATCH,
  RANK_TABS,
  compareFocused,
  compareJevResults,
  focusScore,
  starsOf,
  type CriterionMark,
  type JevPersonResult,
  type RankTab,
  type RankingCriterionView,
  type RankingRow,
} from '@nest-recruit/shared';

// RNK-01's ordering, done in the browser so a tab, a sort or a focus is instant (§L30, F23.04:
// under 100 ms for 1,000 rows, with no request). Pure, so it is unit-tested.

export const SCORE_SORT = 'score';

export interface RankingSort {
  /** `score`, or a criterion's key (AT-5: click a column to sort by that criterion). */
  readonly by: string;
  readonly direction: 'asc' | 'desc';
}

export const DEFAULT_SORT: RankingSort = { by: SCORE_SORT, direction: 'desc' };

export interface RankedListRow extends RankingRow {
  /** Place in the tab, 1 first. */
  readonly rank: number;
  /** The focus score, when focusing (F23.04). */
  readonly focus: number | null;
}

export interface RankingFilter {
  /** `all` lists everyone: a job still in its old order (ADR 0062) has no profile tabs. */
  readonly tab: RankTab | 'all';
  readonly focus: ReadonlySet<string>;
  readonly sort: RankingSort;
  readonly query: string;
}

/** One tab's rows, searched, focused and sorted, numbered from 1. */
export function rankedRows(rows: readonly RankingRow[], criteria: readonly RankingCriterionView[], filter: RankingFilter): readonly RankedListRow[] {
  const query = filter.query.trim().toLowerCase();
  const isFocusing = filter.focus.size > 0;
  const weighted = criteria.map((criterion) => criterion.weight);
  const keys = criteria.map((criterion) => criterion.key);
  const sortIndex = keys.indexOf(filter.sort.by);
  const listed = rows
    .filter((row) => (filter.tab === 'all' || row.tab === filter.tab) && (query === '' || row.name.toLowerCase().includes(query)))
    .map((row) => ({
      row,
      focus: isFocusing ? focusScore(row.cells.map((cell, index) => ({ key: keys[index] ?? '', weight: weighted[index] ?? 0, match: cell.match })), filter.focus) : null,
    }));
  const sign = filter.sort.direction === 'desc' ? 1 : -1;
  listed.sort((a, b) => {
    if (sortIndex >= 0) {
      const byCriterion = (b.row.cells[sortIndex]?.match ?? 0) - (a.row.cells[sortIndex]?.match ?? 0);
      if (byCriterion !== 0) return sign * byCriterion;
    }
    return sign * compareFocused({ focus: a.focus, score: a.row.score ?? -1, order: a.row.order }, { focus: b.focus, score: b.row.score ?? -1, order: b.row.order });
  });
  return listed.map(({ row, focus }, index) => ({ ...row, rank: index + 1, focus }));
}

/**
 * ADR 0050 §4: Jev's order over a tab already ranked — every must-have shown first, then the Jev match, people Jev
 * couldn't read (or who arrived after the ask) at the end; the criteria's order breaks ties. Renumbered from 1;
 * the score, stars and tab of every row stay as the criteria set them.
 */
export function jevOrdered(rows: readonly RankedListRow[], results: ReadonlyMap<string, JevPersonResult>): readonly RankedListRow[] {
  return rows
    .map((row, index) => ({ row, index, result: results.get(row.applicationId) }))
    .sort((a, b) => compareJevResults(a.result, b.result) || a.index - b.index)
    .map(({ row }, index) => ({ ...row, rank: index + 1 }));
}

export function tabCounts(rows: readonly RankingRow[]): Readonly<Record<RankTab, number>> {
  const counts: Record<RankTab, number> = { top_fit: 0, maybe: 0, low_fit: 0, needs_a_look: 0 };
  for (const row of rows) if (row.tab) counts[row.tab] += 1;
  return counts;
}

/** RNK-01 "Filtered empty": the next tab, in order, that has someone in it. */
export function nextTabWithPeople(counts: Readonly<Record<RankTab, number>>, current: RankTab): RankTab | null {
  const start = RANK_TABS.indexOf(current);
  for (let step = 1; step < RANK_TABS.length; step += 1) {
    const tab = RANK_TABS[(start + step) % RANK_TABS.length];
    if (tab && counts[tab] > 0) return tab;
  }
  return null;
}

/** Still open to a review decision, so Shortlist may be offered: the row and the side panel share this (#4a). */
export function isWaitingForReview(row: RankingRow): boolean {
  return row.stage === 'screening' && (row.reviewStatus === null || row.reviewStatus === 'to_review' || row.reviewStatus === 'maybe');
}

/** Other tabs holding a name that matches the search, in tab order (RNK-01 search, #30). */
export function tabsWithMatches(rows: readonly RankingRow[], query: string, current: RankTab): readonly { readonly tab: RankTab; readonly count: number }[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];
  const counts = new Map<RankTab, number>();
  for (const row of rows) {
    if (row.tab && row.tab !== current && row.name.toLowerCase().includes(needle)) counts.set(row.tab, (counts.get(row.tab) ?? 0) + 1);
  }
  return RANK_TABS.flatMap((tab) => {
    const count = counts.get(tab);
    return count ? [{ tab, count }] : [];
  });
}

/**
 * F23.05's optimistic step: one cell set to a mark, the score, stars and tab worked out again
 * from the row's cells with §L1's formula and cap (§L30), so the candidate moves tab at once.
 * The server's answer replaces it; a §L8 penalty, if any, arrives with that answer.
 */
export function withMark(row: RankingRow, criteria: readonly RankingCriterionView[], key: string, mark: CriterionMark, match: number, isCorrected: boolean): RankingRow {
  const index = criteria.findIndex((criterion) => criterion.key === key);
  if (index < 0 || row.cells.length !== criteria.length) return row;
  const cells = row.cells.map((cell, at) => (at === index ? { mark, match, isCorrected } : cell));
  const raw = cells.reduce((sum, cell, at) => sum + cell.match * (criteria[at]?.weight ?? 0), 0);
  const mustHaves = criteria.map((criterion, at) => ({ criterion, cell: cells[at] })).filter(({ criterion }) => criterion.importance === 'must_have');
  const missed = mustHaves.filter(({ cell }) => (cell?.match ?? 0) < MUST_HAVE_MIN_MATCH);
  const score = Math.round(missed.length > 0 ? Math.min(raw, MUST_HAVE_CAP) : raw);
  const stars = starsOf(score);
  const isMustHaveMissing = mustHaves.some(({ cell }) => cell?.mark === 'missing');
  const read = cells.reduce((sum, cell, at) => sum + (cell.mark === 'missing' ? 0 : (criteria[at]?.weight ?? 0)), 0);
  const total = criteria.reduce((sum, criterion) => sum + criterion.weight, 0);
  return {
    ...row,
    cells,
    score,
    stars,
    tab: isMustHaveMissing ? 'needs_a_look' : stars >= 4 ? 'top_fit' : stars === 3 ? 'maybe' : 'low_fit',
    coverage: total === 0 ? 0 : Math.round((100 * read) / total),
    missingMustHaves: missed.map(({ criterion }) => criterion.label),
  };
}

/** Where the open sheet is among the rows on screen, and who is either side (Redesign Group 3, RNK-02). */
export interface SheetPlace {
  readonly position: number;
  readonly total: number;
  readonly previousId: string | null;
  readonly nextId: string | null;
}

/** In the order shown — tab, keywords, filters, focus or Jev's order; undefined when the person isn't on screen. */
export function sheetPlaceOf(rows: readonly Pick<RankingRow, 'applicationId'>[], openedId: string | undefined): SheetPlace | undefined {
  if (openedId === undefined) return undefined;
  const at = rows.findIndex((row) => row.applicationId === openedId);
  if (at < 0) return undefined;
  return { position: at + 1, total: rows.length, previousId: rows[at - 1]?.applicationId ?? null, nextId: rows[at + 1]?.applicationId ?? null };
}

/** A criterion column's tooltip, as the mockup's: "IFRS · Important · weight 16" (Redesign Group 3). */
export function criterionTitle(criterion: RankingCriterionView): string {
  return `${criterion.label} · ${CRITERION_IMPORTANCE_LABELS[criterion.importance]} · weight ${criterion.weight}`;
}
